import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useState, type FormEvent } from 'react'
import SimpleToothChartPicker from '../../pages/manage/treatmenttable/components/treatments/SimpleToothChartPicker'
import LabPatientPicker from './LabPatientPicker'
import {
  applyPriceListItem,
  type LabOrderFormValues,
  type LabPriceListItem,
  computeOrderTotals,
  emptyInvoiceLine,
} from './labOrderFormUtils'

export type LabOrderPartnerOption = {
  id: string
  labId: string | null
  name: string
  address?: string
  phone?: string
  isLocal?: boolean
}

const inputClass =
  'mt-1 w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800'
const labelClass = 'block text-sm font-medium text-slate-600 dark:text-slate-400'

const addFieldLabel =
  'mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400'
const addFieldControl =
  'h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'
const addFieldReadonly =
  'h-10 w-full rounded-lg border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-700/60 dark:text-slate-200'

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h3>
        {description ? (
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function FormBlock({
  step,
  title,
  hint,
  children,
}: {
  step: number
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 dark:border-slate-700/80 dark:bg-slate-800/40">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white shadow-sm">
          {step}
        </span>
        <div className="min-w-0 pt-0.5">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {title}
          </h4>
          {hint ? (
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{hint}</p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  )
}

function formatLkr(amount: number) {
  return `LKR ${(Number(amount) || 0).toLocaleString('en-LK')}`
}

export type ClinicDetails = {
  name: string
  phone?: string
  address?: string
}

function DetailsPanel({
  title,
  name,
  phone,
  address,
}: {
  title: string
  name: string
  phone?: string
  address?: string
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-600 dark:bg-slate-800/50">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        {title}
      </p>
      <dl className="grid gap-3 text-sm">
        <div>
          <dt className="text-slate-500">Name</dt>
          <dd className="font-medium text-slate-900 dark:text-slate-100">{name}</dd>
        </div>
        {phone ? (
          <div>
            <dt className="text-slate-500">Phone</dt>
            <dd className="text-slate-800 dark:text-slate-200">{phone}</dd>
          </div>
        ) : null}
        {address ? (
          <div>
            <dt className="text-slate-500">Address</dt>
            <dd className="text-slate-800 dark:text-slate-200">{address}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  )
}

function LabDetailsPanel({ partner }: { partner: LabOrderPartnerOption }) {
  return (
    <DetailsPanel
      title="Lab details"
      name={partner.name}
      phone={partner.phone}
      address={partner.address}
    />
  )
}

type LabOrderFormProps = {
  partners: LabOrderPartnerOption[]
  values: LabOrderFormValues
  onChange: (values: LabOrderFormValues) => void
  onSubmit: (e: FormEvent) => void
  submitting: boolean
  submitLabel: string
  submittingLabel?: string
  onCancel?: () => void
  partnerLabel?: string
  clinicDetails?: ClinicDetails
  priceListItems?: LabPriceListItem[]
  priceListLoading?: boolean
  patientError?: boolean
  /** Flatter layout for dialogs — lab first, fewer nested cards. */
  variant?: 'page' | 'modal'
  /** Hide bottom Cancel/Submit so a parent modal can own a sticky footer. */
  hideActions?: boolean
  formId?: string
  /** Lock patient (e.g. treatment Send to lab — patient comes from the treatment). */
  patientReadOnly?: boolean
}

export default function LabOrderForm({
  partners,
  values,
  onChange,
  onSubmit,
  submitting,
  submitLabel,
  submittingLabel = 'Sending…',
  onCancel,
  partnerLabel = 'Lab',
  clinicDetails,
  priceListItems = [],
  priceListLoading = false,
  patientError = false,
  variant = 'page',
  hideActions = false,
  formId,
  patientReadOnly = false,
}: LabOrderFormProps) {
  const isModal = variant === 'modal'
  const totals = computeOrderTotals(values)
  const selectedPartner = partners.find((p) => p.id === values.labPartnerId)
  const isManualItems = selectedPartner?.isLocal === true

  function patch(partial: Partial<LabOrderFormValues>) {
    onChange({ ...values, ...partial })
  }

  type LabItemDraft = {
    catalogItemId: string
    item: string
    description: string
    notes: string
    price: number
  }

  const emptyDraft = (): LabItemDraft => ({
    catalogItemId: '',
    item: '',
    description: '',
    notes: '',
    price: 0,
  })

  const [draft, setDraft] = useState<LabItemDraft>(emptyDraft)
  const [addItemError, setAddItemError] = useState('')
  const [toothError, setToothError] = useState(false)

  useEffect(() => {
    setDraft(emptyDraft())
    setAddItemError('')
  }, [values.labPartnerId])

  useEffect(() => {
    if (values.toothNos.length > 0) setToothError(false)
  }, [values.toothNos])

  function selectPartner(partnerId: string) {
    const partner = partners.find((p) => p.id === partnerId)
    onChange({
      ...values,
      labPartnerId: partnerId,
      labId: partner?.labId ? String(partner.labId) : '',
      invoiceItems: [],
    })
  }

  function selectDraftItem(itemId: string) {
    setAddItemError('')
    const opt = priceListItems.find((p) => p.id === itemId)
    if (!opt) {
      setDraft(emptyDraft())
      return
    }
    const line = applyPriceListItem(emptyInvoiceLine(), opt)
    setDraft({
      catalogItemId: itemId,
      item: line.item,
      description: line.description,
      notes: draft.notes,
      price: line.price,
    })
  }

  function addDraftToTable() {
    setAddItemError('')
    if (isManualItems) {
      if (!draft.item.trim()) {
        setAddItemError('Enter an item name')
        return
      }
    } else if (!draft.catalogItemId || !draft.item.trim()) {
      setAddItemError('Select a lab item first')
      return
    }
    const duplicate = values.invoiceItems.some(
      (row) =>
        row.item === draft.item &&
        row.description === draft.description &&
        row.notes.trim() === draft.notes.trim(),
    )
    if (duplicate) {
      setAddItemError('This lab item is already in the table')
      return
    }
    patch({
      invoiceItems: [
        ...values.invoiceItems,
        {
          item: draft.item,
          description: draft.description,
          notes: draft.notes.trim(),
          price: draft.price,
          lineDiscount: 0,
        },
      ],
    })
    setDraft(emptyDraft())
  }

  function removeLabItem(index: number) {
    patch({
      invoiceItems: values.invoiceItems.filter((_, i) => i !== index),
    })
  }

  const tableItems = values.invoiceItems.filter((line) => line.item.trim())
  const itemCount = tableItems.length
  const canSubmit = Boolean(values.labPartnerId) && !submitting

  function onPatientSelect(patient: string, patientMeta: string) {
    const next: Partial<LabOrderFormValues> = { patient, patientMeta }
    if (!values.caseTitle.trim()) {
      const regMatch = patient.match(/\(([^)]+)\)\s*$/)
      if (regMatch?.[1]) {
        next.caseTitle = `Case — ${regMatch[1]}`
      }
    }
    patch(next)
  }

  const patientField = (
    <>
      <LabPatientPicker
        patientLabel={values.patient}
        patientMeta={values.patientMeta}
        showError={patientError}
        readOnly={patientReadOnly}
        onSelect={onPatientSelect}
        onClear={
          patientReadOnly
            ? undefined
            : () => patch({ patient: '', patientMeta: '' })
        }
      />
      <input
        type="hidden"
        name="patient"
        value={values.patient}
        required
        readOnly
        aria-hidden
        tabIndex={-1}
      />
    </>
  )

  const caseDetailFields = (
    <div className="space-y-4">
      <div>
        <label htmlFor="case-title" className={addFieldLabel}>
          Case title <span className="text-primary">*</span>
        </label>
        <input
          id="case-title"
          required
          value={values.caseTitle}
          onChange={(e) => patch({ caseTitle: e.target.value })}
          placeholder="e.g. Upper zirconia crown"
          className={addFieldControl}
        />
      </div>
      <div>
        <p className={addFieldLabel}>
          Teeth <span className="text-primary">*</span>
        </p>
        <SimpleToothChartPicker
          value={values.toothNos}
          required
          error={toothError}
          onChange={(teeth) => {
            setToothError(false)
            patch({ toothNos: teeth })
          }}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="case-meta" className={addFieldLabel}>
            Work type
          </label>
          <input
            id="case-meta"
            value={values.caseMeta}
            onChange={(e) => patch({ caseMeta: e.target.value })}
            placeholder="e.g. Implant, screw-retained"
            className={addFieldControl}
          />
        </div>
        <div>
          <label htmlFor="case-detail" className={addFieldLabel}>
            Notes for lab
          </label>
          <input
            id="case-detail"
            value={values.caseDetail}
            onChange={(e) => patch({ caseDetail: e.target.value })}
            placeholder="Shade, material, special instructions…"
            className={addFieldControl}
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="order-date" className={addFieldLabel}>
            Order date
          </label>
          <input
            id="order-date"
            type="date"
            value={values.orderDate}
            onChange={(e) => patch({ orderDate: e.target.value })}
            className={addFieldControl}
          />
        </div>
        <div>
          <label htmlFor="due-date" className={addFieldLabel}>
            Due date
          </label>
          <input
            id="due-date"
            type="date"
            value={values.dueDate}
            onChange={(e) => patch({ dueDate: e.target.value })}
            min={values.orderDate || undefined}
            className={addFieldControl}
          />
        </div>
      </div>
    </div>
  )

  function handleSubmit(e: FormEvent) {
    if (!values.toothNos.length) {
      e.preventDefault()
      setToothError(true)
      return
    }
    onSubmit(e)
  }

  const labSelect = (
    <div className="space-y-3">
      <label className={labelClass} htmlFor="lab-partner">
        {partnerLabel} <span className="text-primary">*</span>
        <select
          id="lab-partner"
          required
          value={values.labPartnerId}
          onChange={(e) => selectPartner(e.target.value)}
          className={inputClass}
        >
          {partners.length === 0 ? (
            <option value="">No labs available</option>
          ) : null}
          {partners.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.isLocal ? ' (personal)' : ''}
            </option>
          ))}
        </select>
      </label>
      {isModal && selectedPartner ? (
        <p className="text-xs text-slate-500">
          {[selectedPartner.phone, selectedPartner.address]
            .filter(Boolean)
            .join(' · ') || 'Partner lab'}
          {clinicDetails?.name ? ` · From ${clinicDetails.name}` : ''}
        </p>
      ) : null}
    </div>
  )

  const addedItemRows = values.invoiceItems
    .map((line, rowIndex) => ({ line, rowIndex }))
    .filter(({ line }) => line.item.trim())

  const modalItemsList =
    tableItems.length === 0 ? (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center dark:border-slate-600 dark:bg-slate-800/40">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          No items added yet
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Pick a lab item below and tap Add.
        </p>
      </div>
    ) : (
      <ul className="space-y-2">
        {addedItemRows.map(({ line, rowIndex }, displayIndex) => (
          <li
            key={`${line.item}-${rowIndex}`}
            className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3 dark:border-slate-600 dark:bg-slate-800/80"
          >
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              {displayIndex + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {line.item}
                </p>
                <p className="shrink-0 text-sm font-semibold tabular-nums text-primary">
                  {formatLkr(line.price)}
                </p>
              </div>
              {line.description ? (
                <p className="mt-0.5 text-xs text-slate-500">{line.description}</p>
              ) : null}
              {line.notes ? (
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  Note: {line.notes}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => removeLabItem(rowIndex)}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
              aria-label={`Remove ${line.item}`}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </button>
          </li>
        ))}
      </ul>
    )

  const modalCatalogAdd = (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-600 dark:bg-slate-800/50">
      <div>
        <label htmlFor="draft-lab-item" className={addFieldLabel}>
          Lab item <span className="text-primary">*</span>
        </label>
        <select
          id="draft-lab-item"
          value={draft.catalogItemId}
          onChange={(e) => selectDraftItem(e.target.value)}
          className={addFieldControl}
        >
          <option value="">Select from lab catalog…</option>
          {priceListItems.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label} — {formatLkr(p.basePrice)}
            </option>
          ))}
        </select>
      </div>

      {draft.catalogItemId ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 dark:bg-primary/10">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {draft.item}
            </p>
            {draft.description ? (
              <p className="truncate text-xs text-slate-500">{draft.description}</p>
            ) : null}
          </div>
          <p className="shrink-0 text-sm font-semibold tabular-nums text-primary">
            {formatLkr(draft.price)}
          </p>
        </div>
      ) : null}

      <div>
        <label htmlFor="draft-note" className={addFieldLabel}>
          Note <span className="normal-case tracking-normal">(optional)</span>
        </label>
        <input
          id="draft-note"
          type="text"
          value={draft.notes}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, notes: e.target.value }))
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addDraftToTable()
            }
          }}
          placeholder="Shade, special request…"
          disabled={!draft.catalogItemId}
          className={`${addFieldControl} disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:disabled:bg-slate-800/50`}
        />
      </div>

      {addItemError ? (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {addItemError}
        </p>
      ) : null}

      <button
        type="button"
        onClick={addDraftToTable}
        disabled={!draft.catalogItemId}
        className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-45"
      >
        <AddIcon sx={{ fontSize: 18 }} />
        Add item
      </button>
    </div>
  )

  const modalManualAdd = (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-600 dark:bg-slate-800/50">
      <p className="text-xs text-slate-500">
        Personal lab — enter items yourself.
      </p>
      <div>
        <label htmlFor="manual-item" className={addFieldLabel}>
          Item <span className="text-primary">*</span>
        </label>
        <input
          id="manual-item"
          type="text"
          value={draft.item}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, item: e.target.value }))
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addDraftToTable()
            }
          }}
          placeholder="e.g. Zirconia crown"
          className={addFieldControl}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7.5rem]">
        <div className="min-w-0">
          <label htmlFor="manual-description" className={addFieldLabel}>
            Description{' '}
            <span className="normal-case tracking-normal">(optional)</span>
          </label>
          <input
            id="manual-description"
            type="text"
            value={draft.description}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            placeholder="Optional details"
            className={addFieldControl}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="manual-price" className={addFieldLabel}>
            Price
          </label>
          <input
            id="manual-price"
            type="number"
            min={0}
            step={0.01}
            value={draft.price || ''}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                price: Number(e.target.value) || 0,
              }))
            }
            className={addFieldControl}
          />
        </div>
      </div>
      {addItemError ? (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {addItemError}
        </p>
      ) : null}
      <button
        type="button"
        onClick={addDraftToTable}
        className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95"
      >
        <AddIcon sx={{ fontSize: 18 }} />
        Add item
      </button>
    </div>
  )

  const modalItemsBody = !values.labPartnerId ? (
    <p className="text-sm text-slate-500">Select a lab first.</p>
  ) : isManualItems ? (
    <div className="space-y-4">
      {modalManualAdd}
      {modalItemsList}
    </div>
  ) : priceListLoading ? (
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-6 text-sm text-slate-500 dark:border-slate-600">
      <span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      Loading lab catalog…
    </div>
  ) : priceListItems.length === 0 ? (
    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
      This lab has no priced items yet. Ask them to add items in their portal,
      or pick another lab.
    </p>
  ) : (
    <div className="space-y-4">
      {modalCatalogAdd}
      {modalItemsList}
    </div>
  )

  const itemsBody = !values.labPartnerId ? (
    <p className="text-sm text-slate-500">Select a lab first.</p>
  ) : isManualItems ? (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Add items manually — this lab is tracked by your clinic only.
      </p>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/80 sm:p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7.5rem_auto] lg:items-end lg:gap-3">
          <div className="min-w-0">
            <label htmlFor="manual-item-page" className={addFieldLabel}>
              Item <span className="text-primary">*</span>
            </label>
            <input
              id="manual-item-page"
              type="text"
              value={draft.item}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, item: e.target.value }))
              }
              placeholder="e.g. Zirconia crown"
              className={addFieldControl}
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="manual-description-page" className={addFieldLabel}>
              Description
            </label>
            <input
              id="manual-description-page"
              type="text"
              value={draft.description}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Optional details"
              className={addFieldControl}
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="manual-price-page" className={addFieldLabel}>
              Price
            </label>
            <input
              id="manual-price-page"
              type="number"
              min={0}
              step={0.01}
              value={draft.price || ''}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  price: Number(e.target.value) || 0,
                }))
              }
              className={addFieldControl}
            />
          </div>
          <div className="min-w-0 sm:col-span-2 lg:col-span-1">
            <button
              type="button"
              onClick={addDraftToTable}
              className="h-10 w-full rounded-lg bg-gradient-to-r from-primary to-primaryLight px-5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 lg:w-auto lg:min-w-[8.5rem]"
            >
              Add item
            </button>
          </div>
        </div>
        {addItemError ? (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {addItemError}
          </p>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-700">
              <th className="px-3 py-2.5 font-medium text-slate-500">#</th>
              <th className="px-3 py-2.5 font-medium text-slate-500">Item</th>
              <th className="px-3 py-2.5 font-medium text-slate-500">
                Description
              </th>
              <th className="px-3 py-2.5 font-medium text-slate-500">Price</th>
              <th className="w-20 px-3 py-2.5 font-medium text-slate-500">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tableItems.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-6 text-center text-slate-500"
                >
                  No items yet — add at least one to send the order.
                </td>
              </tr>
            ) : (
              addedItemRows.map(({ line, rowIndex }, displayIndex) => (
                <tr
                  key={`${line.item}-${rowIndex}`}
                  className="border-b border-slate-100 last:border-b-0 dark:border-slate-800"
                >
                  <td className="px-3 py-2.5 text-slate-500">
                    {displayIndex + 1}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-slate-900 dark:text-slate-100">
                    {line.item}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    {line.description || '—'}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-slate-900 dark:text-slate-100">
                    {formatLkr(line.price)}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => removeLabItem(rowIndex)}
                      className="text-xs font-medium text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {tableItems.length > 0 ? (
        <p className="text-right text-sm text-slate-600">
          Total{' '}
          <span className="font-bold text-primary">
            {formatLkr(totals.finalTotal)}
          </span>
        </p>
      ) : null}
    </div>
  ) : priceListLoading ? (
    <p className="text-sm text-slate-500">Loading lab catalog…</p>
  ) : priceListItems.length === 0 ? (
    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
      This lab has no priced items yet. Ask them to add items in their portal,
      or pick another lab.
    </p>
  ) : (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800/80 sm:p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1.15fr)_7.5rem_auto] lg:items-end lg:gap-3">
          <div className="min-w-0">
            <label htmlFor="draft-lab-item-page" className={addFieldLabel}>
              Lab item <span className="text-primary">*</span>
            </label>
            <select
              id="draft-lab-item-page"
              value={draft.catalogItemId}
              onChange={(e) => selectDraftItem(e.target.value)}
              className={addFieldControl}
            >
              <option value="">Select lab item…</option>
              {priceListItems.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <label htmlFor="draft-description-page" className={addFieldLabel}>
              Description
            </label>
            <input
              id="draft-description-page"
              readOnly
              value={draft.description}
              placeholder="From lab catalog"
              className={addFieldReadonly}
            />
          </div>
          <div className="min-w-0 sm:col-span-2 lg:col-span-1">
            <label htmlFor="draft-note-page" className={addFieldLabel}>
              Note
            </label>
            <input
              id="draft-note-page"
              type="text"
              value={draft.notes}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder={
                draft.catalogItemId
                  ? 'Optional note for this item'
                  : 'Select a lab item first'
              }
              disabled={!draft.catalogItemId}
              className={`${addFieldControl} disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 dark:disabled:bg-slate-800/50`}
            />
          </div>
          <div className="min-w-0">
            <span className={addFieldLabel}>Price</span>
            <div
              className={`flex h-10 items-center justify-end rounded-lg border px-3 text-sm font-semibold tabular-nums ${
                draft.item
                  ? 'border-primary/30 bg-primary/5 text-primary dark:bg-primary/10'
                  : 'border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-600 dark:bg-slate-800'
              }`}
            >
              {draft.item ? formatLkr(draft.price) : '—'}
            </div>
          </div>
          <div className="min-w-0 sm:col-span-2 lg:col-span-1">
            <span className={`${addFieldLabel} hidden lg:block`} aria-hidden>
              &nbsp;
            </span>
            <button
              type="button"
              onClick={addDraftToTable}
              disabled={!draft.catalogItemId}
              className="h-10 w-full rounded-lg bg-gradient-to-r from-primary to-primaryLight px-5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-45 lg:w-auto lg:min-w-[8.5rem] lg:whitespace-nowrap"
            >
              Add item
            </button>
          </div>
        </div>
        {addItemError ? (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {addItemError}
          </p>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-700">
              <th className="px-3 py-2.5 font-medium text-slate-500">#</th>
              <th className="px-3 py-2.5 font-medium text-slate-500">
                Lab item
              </th>
              <th className="px-3 py-2.5 font-medium text-slate-500">
                Description
              </th>
              <th className="px-3 py-2.5 font-medium text-slate-500">Note</th>
              <th className="px-3 py-2.5 font-medium text-slate-500">Price</th>
              <th className="w-20 px-3 py-2.5 font-medium text-slate-500">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tableItems.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-6 text-center text-slate-500"
                >
                  No items yet — select a catalog item and click Add item.
                </td>
              </tr>
            ) : (
              addedItemRows.map(({ line, rowIndex }, displayIndex) => (
                <tr
                  key={`${line.item}-${rowIndex}`}
                  className="border-b border-slate-100 last:border-b-0 dark:border-slate-800"
                >
                  <td className="px-3 py-2.5 text-slate-500">
                    {displayIndex + 1}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-slate-900 dark:text-slate-100">
                    {line.item}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    {line.description || '—'}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    {line.notes || '—'}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-slate-900 dark:text-slate-100">
                    {formatLkr(line.price)}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => removeLabItem(rowIndex)}
                      className="text-xs font-medium text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {tableItems.length > 0 ? (
        <p className="text-right text-sm text-slate-600">
          Total{' '}
          <span className="font-bold text-primary">
            {formatLkr(totals.finalTotal)}
          </span>
        </p>
      ) : null}
    </div>
  )

  const actions = hideActions ? null : (
    <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 dark:border-slate-700 sm:flex-row sm:justify-end">
      {onCancel ? (
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
        >
          Cancel
        </button>
      ) : null}
      <button
        type="submit"
        disabled={!canSubmit}
        className="rounded-lg bg-gradient-to-r from-primary to-primaryLight px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:opacity-60 sm:min-w-[10rem]"
      >
        {submitting ? submittingLabel : submitLabel}
      </button>
    </div>
  )

  if (isModal) {
    return (
      <form
        id={formId}
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              1. Choose lab
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              The order will be sent to this partner.
            </p>
          </div>
          {labSelect}
        </section>

        <section className="space-y-3 border-t border-slate-100 pt-5 dark:border-slate-700/80">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              2. {patientReadOnly ? 'Case details' : 'Patient & case'}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {patientReadOnly
                ? 'Patient comes from this treatment — describe the lab work.'
                : 'Confirm the patient and describe the work.'}
            </p>
          </div>
          <div className="space-y-4">
            {patientField}
            {caseDetailFields}
          </div>
        </section>

        <section className="space-y-3 border-t border-slate-100 pt-5 dark:border-slate-700/80">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                3. Lab items
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Select from the lab catalog, add a note if needed, then Add.
              </p>
            </div>
            {itemCount > 0 ? (
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                {itemCount} item{itemCount === 1 ? '' : 's'} ·{' '}
                {formatLkr(totals.finalTotal)}
              </span>
            ) : null}
          </div>
          {modalItemsBody}
        </section>

        {actions}
      </form>
    )
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-5">
      <Section
        title="Case & patient"
        description="Select the patient, describe the lab work, and set expected dates."
      >
        <div className="space-y-4">
          <FormBlock
            step={1}
            title="Patient"
            hint="Search existing patients or add a new one — then tap to select."
          >
            {patientField}
          </FormBlock>

          <FormBlock
            step={2}
            title="Case details"
            hint="What work is being sent to the lab?"
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="case-title-page" className={addFieldLabel}>
                  Case title <span className="text-primary">*</span>
                </label>
                <input
                  id="case-title-page"
                  required
                  value={values.caseTitle}
                  onChange={(e) => patch({ caseTitle: e.target.value })}
                  placeholder="e.g. Upper zirconia crown"
                  className={addFieldControl}
                />
              </div>
              <div>
                <p className={addFieldLabel}>
                  Teeth <span className="text-primary">*</span>
                </p>
                <SimpleToothChartPicker
                  value={values.toothNos}
                  required
                  error={toothError}
                  onChange={(teeth) => {
                    setToothError(false)
                    patch({ toothNos: teeth })
                  }}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="case-meta-page" className={addFieldLabel}>
                    Work type
                  </label>
                  <input
                    id="case-meta-page"
                    value={values.caseMeta}
                    onChange={(e) => patch({ caseMeta: e.target.value })}
                    placeholder="e.g. Implant, screw-retained"
                    className={addFieldControl}
                  />
                </div>
                <div>
                  <label htmlFor="case-detail-page" className={addFieldLabel}>
                    Notes for lab
                  </label>
                  <input
                    id="case-detail-page"
                    value={values.caseDetail}
                    onChange={(e) => patch({ caseDetail: e.target.value })}
                    placeholder="Shade, material, special instructions…"
                    className={addFieldControl}
                  />
                </div>
              </div>
            </div>
          </FormBlock>

          <FormBlock
            step={3}
            title="Dates"
            hint="When the order is placed and when you need it back."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="order-date-page" className={addFieldLabel}>
                  Order date
                </label>
                <input
                  id="order-date-page"
                  type="date"
                  value={values.orderDate}
                  onChange={(e) => patch({ orderDate: e.target.value })}
                  className={addFieldControl}
                />
              </div>
              <div>
                <label htmlFor="due-date-page" className={addFieldLabel}>
                  Due date
                </label>
                <input
                  id="due-date-page"
                  type="date"
                  value={values.dueDate}
                  onChange={(e) => patch({ dueDate: e.target.value })}
                  min={values.orderDate || undefined}
                  className={addFieldControl}
                />
              </div>
            </div>
          </FormBlock>
        </div>
      </Section>

      <Section title={partnerLabel}>
        {labSelect}
        <div className="grid gap-3 lg:grid-cols-2">
          {clinicDetails?.name ? (
            <DetailsPanel
              title="Clinic (sender)"
              name={clinicDetails.name}
              phone={clinicDetails.phone}
              address={clinicDetails.address}
            />
          ) : null}
          {selectedPartner ? <LabDetailsPanel partner={selectedPartner} /> : null}
        </div>
      </Section>

      <Section title="Lab item">{itemsBody}</Section>

      {actions}
    </form>
  )
}
